// krida-ui-collection.js - Collection screen module for dashboard
// Displays all 14 themes and special cards with lock/unlock states

import { getPlayerProgression } from './player-stats-client.js';
import { CARD_DESCRIPTIONS } from './krida-ui-special-cards.js';
import { ThemeManager } from './theme-manager.js';

// Hardcoded progression display metadata (Option A from Dev Notes)
// Source of truth: functions/seed-progression-config.js
const PROGRESSION_LEVELS = [
  { level: 1, theme: 'beach', specialCard: 'the-scout', phase: 'Basics', tier: 'free' },
  { level: 2, theme: 'sunset', specialCard: 'the-gardener', phase: 'Basics', tier: 'free' },
  { level: 3, theme: 'night', specialCard: 'the-shepherd', phase: 'Basics', tier: 'free' },
  { level: 4, theme: 'sunrise', specialCard: 'the-merchant', phase: 'Exploring', tier: 'free' },
  { level: 5, theme: 'mountains', specialCard: 'the-swap', phase: 'Exploring', tier: 'free' },
  { level: 6, theme: 'forest', specialCard: 'the-reaper', phase: 'Exploring', tier: 'free' },
  { level: 7, theme: 'pride', specialCard: 'the-oracle', phase: 'Growing', tier: 'paid' },
  { level: 8, theme: 'independence', specialCard: 'the-tempest', phase: 'Growing', tier: 'paid' },
  { level: 9, theme: 'halloween', specialCard: 'the-thief', phase: 'Growing', tier: 'paid' },
  { level: 10, theme: 'christmas', specialCard: 'the-flood', phase: 'Growing', tier: 'paid' },
  { level: 11, theme: 'mexico', specialCard: 'the-plague', phase: 'Mastery', tier: 'paid' },
  { level: 12, theme: 'egypt', specialCard: 'the-crown', phase: 'Mastery', tier: 'paid' },
  { level: 13, theme: 'japan', specialCard: null, phase: 'Mastery', tier: 'paid' },
  { level: 14, theme: 'ancient', specialCard: null, phase: 'Mastery', tier: 'paid' },
];

// Theme card-back image paths (only themes with actual assets)
const THEME_CARD_BACKS = {
  'beach': '/themes/beach/images/card-back.png',
  'sunset': '/themes/sunset/images/card-back.png',
  'night': '/themes/night/images/AdobeStock_1214653550.jpeg',
  'sunrise': '/themes/sunrise/images/card-back.png',
  'mountains': '/themes/mountains/images/card-back.png',
  'forest': '/themes/forest/images/card-back.png',
  'pride': '/themes/pride/images/card-back.png',
  'independence': '/themes/independence/images/card-back.png',
  'halloween': '/themes/halloween/images/card-back.png',
  'christmas': '/themes/christmas/images/card-back.png',
  'mexico': '/themes/mexico/images/card-back.png',
  'egypt': '/themes/egypt/images/card-back.png',
  'japan': '/themes/japan/images/card-back.png',
  'ancient': '/themes/ancient/images/card-back.png',
};

let collectionPanel = null;
let loaded = false;

function init() {
  collectionPanel = document.getElementById('collection-panel');
  if (!collectionPanel) {
    console.error('KridaUICollection: #collection-panel not found');
    return;
  }

  // Lazy-load: listen for tab activation
  document.addEventListener('click', handleTabClick);
  console.log('KridaUICollection: Initialized, waiting for tab activation.');
}

function handleTabClick(e) {
  const btn = e.target.closest('.tab-button');
  if (!btn || btn.dataset.tab !== 'collection-panel') return;
  if (!loaded) {
    loadCollection();
  }
}

async function loadCollection() {
  if (loaded || !collectionPanel) return;
  loaded = true;

  collectionPanel.innerHTML = '<p class="collection-loading">Loading collection...</p>';

  let progression = null;
  try {
    progression = await getPlayerProgression();
  } catch (err) {
    console.warn('KridaUICollection: Could not load progression, showing all as locked.', err);
  }

  const unlockedThemes = progression?.unlockedThemes || ['beach'];
  const unlockedCards = progression?.unlockedSpecialCards || [];

  render(unlockedThemes, unlockedCards);
}

function render(unlockedThemes, unlockedCards) {
  if (!collectionPanel) return;

  const html = `
    <div class="collection-section">
      <h3 class="collection-section-header">Themes</h3>
      <div class="collection-grid collection-themes-grid">
        ${PROGRESSION_LEVELS.map(lvl => renderThemeCard(lvl, unlockedThemes)).join('')}
      </div>
    </div>
    <div class="collection-section">
      <h3 class="collection-section-header">Special Cards</h3>
      <div class="collection-grid collection-cards-grid">
        ${PROGRESSION_LEVELS.map(lvl => renderSpecialCard(lvl, unlockedCards)).join('')}
      </div>
    </div>
  `;

  collectionPanel.innerHTML = html;

  // Mark the currently active theme
  markActiveTheme(ThemeManager.getCurrentTheme());

  // Theme click handlers (event delegation on themes grid)
  const themesGrid = collectionPanel.querySelector('.collection-themes-grid');
  if (themesGrid) {
    themesGrid.addEventListener('click', (e) => {
      const item = e.target.closest('.collection-item');
      if (!item) return;

      if (item.classList.contains('unlocked')) {
        ThemeManager.setTheme(item.dataset.theme);
      } else {
        pulseLockedItem(item);
      }
    });
  }

  // Special cards click handlers (event delegation on cards grid)
  const cardsGrid = collectionPanel.querySelector('.collection-cards-grid');
  if (cardsGrid) {
    cardsGrid.addEventListener('click', (e) => {
      const item = e.target.closest('.collection-item');
      if (!item) return;

      if (item.classList.contains('locked')) {
        pulseLockedItem(item);
      }
    });
  }

  // Listen for theme changes from elsewhere (e.g., options modal)
  document.addEventListener('theme-changed', (e) => {
    markActiveTheme(e.detail?.themeName);
  });
}

function markActiveTheme(themeName) {
  if (!collectionPanel) return;
  const items = collectionPanel.querySelectorAll('.collection-themes-grid .collection-item');
  items.forEach(item => {
    item.classList.toggle('active-theme', item.dataset.theme === themeName);
  });
}

function pulseLockedItem(item) {
  const lock = item.querySelector('.lock-indicator');
  if (!lock) return;
  lock.classList.add('highlighted');
  setTimeout(() => lock.classList.remove('highlighted'), 600);
}

function renderThemeCard(levelData, unlockedThemes) {
  const { level, theme, phase } = levelData;
  const isUnlocked = unlockedThemes.includes(theme);
  const stateClass = isUnlocked ? 'unlocked' : 'locked';
  const displayName = formatThemeName(theme);
  const cardBackSrc = THEME_CARD_BACKS[theme] || null;

  const thumbnail = cardBackSrc
    ? `<img class="collection-item-thumbnail" src="${cardBackSrc}" alt="${displayName} card back" loading="lazy">`
    : `<div class="collection-item-thumbnail collection-placeholder-thumbnail"><span>?</span></div>`;

  const lockOverlay = isUnlocked
    ? ''
    : `<div class="lock-indicator">
        <span class="lock-icon">&#x1F512;</span>
        <span class="lock-text">Unlocks at Level ${level}</span>
      </div>`;

  return `
    <div class="collection-item ${stateClass}" data-theme="${theme}" data-level="${level}">
      ${thumbnail}
      <div class="collection-item-info">
        <span class="collection-item-name">${displayName}</span>
        <span class="collection-item-phase">${phase}</span>
      </div>
      ${lockOverlay}
    </div>
  `;
}

function renderSpecialCard(levelData, unlockedCards) {
  const { level, specialCard, phase } = levelData;
  const isUnlocked = unlockedCards.includes(specialCard);
  const stateClass = isUnlocked ? 'unlocked' : 'locked';
  const displayName = formatCardName(specialCard);
  const description = CARD_DESCRIPTIONS[specialCard] || 'Coming Soon';

  const lockOverlay = isUnlocked
    ? ''
    : `<div class="lock-indicator">
        <span class="lock-icon">&#x1F512;</span>
        <span class="lock-text">Unlocks at Level ${level}</span>
      </div>`;

  return `
    <div class="collection-item collection-special-card ${stateClass}" data-card="${specialCard}" data-level="${level}">
      <div class="collection-card-icon">&#x2605;</div>
      <div class="collection-item-info">
        <span class="collection-item-name">${displayName}</span>
        <span class="collection-card-desc">${description}</span>
        <span class="collection-item-phase">${phase}</span>
      </div>
      ${lockOverlay}
    </div>
  `;
}

function formatThemeName(theme) {
  if (theme.startsWith('placeholder-')) {
    return `Theme ${theme.split('-')[1]}`;
  }
  return theme.charAt(0).toUpperCase() + theme.slice(1);
}

function formatCardName(cardId) {
  if (cardId.startsWith('wild-')) {
    return `Card ${cardId.split('-')[1]}`;
  }
  return cardId
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Auto-initialize when module loads
init();
